import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/index';
import { allProjects } from '@/mocks/projects';

const categoryMap: Record<string, string> = {
  'All': 'projects_cat_all',
  'Pharmaceutical': 'projects_cat_pharma',
  'Defence Industry': 'projects_cat_defense',
  'Textile': 'projects_cat_textile',
  'Industrial Production': 'projects_cat_industrial',
};

const featuredIds = [
  'tai-b320-ileri-teknoloji-kompozit-uretim-fabrikasi',
  'abdi-ibrahim-bio-fabrikasi',
  'aselsan-akyurt-tesisi',
  'abdi-ibrahim-yeni-uretim-fabrikasi-yut',
  'sivas-yasin-tekstil-fabrikasi',
  'mustafa-nevzat-ilac-fabrikasi',
];

const featuredProjects = allProjects.filter((p) => featuredIds.includes(p.id));

export default function Projects() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('All');
  const isEn = i18n.language === 'en';

  const categories = Object.keys(categoryMap);

  const filtered = activeCategory === 'All'
    ? featuredProjects
    : featuredProjects.filter((p) => p.categoryEn === activeCategory);

  return (
    <section className="py-24 bg-white" id="projects">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase">{t('projects_label')}</span>
            <h2 className="text-3xl md:text-4xl font-black text-[#1A2332] mt-3">
              {t('projects_title')}
            </h2>
          </div>
          <Link to="/projects" className="text-sm text-[#E8A020] font-semibold hover:underline cursor-pointer whitespace-nowrap self-start md:self-auto">
            {t('projects_view_all')} <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-[#1A2332] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t(categoryMap[cat])}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project) => {
            const displayTitle = isEn ? project.titleEn : project.title;
            const displayTags = isEn ? project.tagsEn : project.tags;
            const displayCategory = isEn ? project.categoryEn : project.category;

            return (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="group rounded-lg overflow-hidden border border-gray-100 hover:border-[#E8A020]/30 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={project.image}
                    alt={displayTitle}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-[#E8A020] text-white text-xs font-bold px-3 py-1 rounded-full">
                      {displayCategory}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="bg-black/50 text-white text-xs px-2.5 py-1 rounded-full">{project.year}</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="bg-white/90 text-[#1A2332] text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5">
                      <i className="ri-eye-line" />
                      {t('projects_view_detail_btn')}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1A2332] text-base mb-2 leading-snug group-hover:text-[#E8A020] transition-colors">{displayTitle}</h3>
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-map-pin-line" /> {project.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-layout-grid-line" /> {project.area}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {displayTags.map((tag) => (
                      <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
